// Tanıtım sayfası. Burada yazan her özellik üründe vardır; yeni bir iddia eklerken önce bunu doğrulayın.
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type { IconType } from 'react-icons';
import { LuArrowRight, LuFileDown, LuHand, LuMonitorSmartphone, LuPenLine, LuRefreshCw, LuSigma } from 'react-icons/lu';
import { ANNOTATION_FONT, POLYGONS } from '../../shared/geometry';
import { layoutCoordinatePlane, layoutFormula, layoutNumberLine, type Figure } from '../../shared/math';
import type { PolygonKind } from '../../shared/protocol';
import { NovaMark } from '../components/ui';
import { measureText } from '../lib/measure';
import { extractLessonId } from '../lib/util';

const LOGIN_PATH = '/giris';
const SIGNUP_PATH = '/kayit';
const SUPERNOVA_URL = 'https://supernova-academy.org';

const PAINS: { problem: string; answer: string }[] = [
  {
    problem: 'Ekran paylaşımında yazı bulanıklaşır; zayıf internette ders takılır.',
    answer: 'Video gönderilmez. Herkes PDF’yi kendi cihazında açar, yalnızca kalem hareketleri iletilir. Sayfa, her ekranda net kalır.',
  },
  {
    problem: 'Öğrenci derse geç kalır ya da bağlantısı kopar, anlatılanı kaçırır.',
    answer: 'Çizimler kaydedilir. Geç gelen veya yeniden bağlanan öğrenci, dersi kaldığı yerden görür.',
  },
  {
    problem: 'Ders bitince tahtadakiler kaybolur; öğrenci not almakla uğraşır.',
    answer: 'Tek tıkla, tüm çizimlerin işlendiği PDF’yi indirin ve öğrencilerinizle paylaşın.',
  },
  {
    problem: 'Ortak tahtada herkes aynı anda çizince ders kontrolden çıkar.',
    answer: 'Öğrenciler varsayılan olarak yalnızca izler. Kimin çizeceğine siz karar verirsiniz.',
  },
];

const STEPS: { title: string; text: string }[] = [
  { title: 'Hesap açıp PDF’nizi yükleyin', text: 'Kayıt olup e-postanızı doğrulayın. Soru kitabı, çalışma kâğıdı ya da ders notu; dosyayı bırakmanız yeterli.' },
  { title: 'Bağlantıyı paylaşın', text: 'Öğrenciler bağlantıya tıklar, adını yazar ve derse girer. Hesap açmaları ya da program kurmaları gerekmez.' },
  { title: 'Kalemle anlatın', text: 'Yazdığınız her çizgi, siz kalemi kaldırmadan öğrencinin ekranında belirir.' },
];

const FEATURES: { icon: IconType; title: string; text: string }[] = [
  { icon: LuPenLine, title: 'Canlı kalem', text: 'Kalem, fosforlu kalem, silgi, ok, şekiller, metin ve lazer işaretçi. Çizdiğiniz an öğrencide görünür.' },
  { icon: LuSigma, title: 'Matematik araçları', text: 'Hazır işaretler, integral ve toplam gibi formüller, sayı doğrusu, koordinat düzlemi ve geometrik şekiller.' },
  { icon: LuHand, title: 'Sınıf sizin kontrolünüzde', text: 'Öğrenci el kaldırır, siz kalemi verirsiniz. Sayfa takibini kilitleyerek herkesi aynı sayfada tutabilirsiniz.' },
  { icon: LuRefreshCw, title: 'Kopmalara dayanıklı', text: 'Bağlantı kopunca kendiliğinden yeniden bağlanır. Sayfayı yenilemek çizimleri silmez.' },
  { icon: LuFileDown, title: 'Çizimli PDF', text: 'Ders notları orijinal PDF’nin üzerine işlenir; PDF’deki metin aranabilir ve kopyalanabilir kalır.' },
  { icon: LuMonitorSmartphone, title: 'Tarayıcıda çalışır', text: 'Kurulum yok. Herkes sayfayı kendi ekran boyutunda görür, yakınlaştırma da kişiye özeldir.' },
];

const FAQ: { q: string; a: string }[] = [
  { q: 'Öğrencilerin hesap açması gerekiyor mu?', a: 'Hayır. Öğrenciler öğretmenin paylaştığı bağlantıyla ya da ders koduyla, yalnızca adını yazarak katılır.' },
  { q: 'Ses ve görüntü var mı?', a: 'Şu an yok. Nova Class yazılı anlatıma odaklanır; sesli görüşme için yanında Meet veya Zoom gibi bir araç kullanabilirsiniz.' },
  { q: 'Bir şey kurmam gerekiyor mu?', a: 'Hayır. Nova Class internet tarayıcısında açılır; öğretmenin ve öğrencinin ek bir program kurmasına gerek yoktur.' },
  { q: 'Nasıl hesap açabilirim?', a: 'Hesap aç düğmesine basıp adınızı, e-postanızı ve şifrenizi yazın, ardından e-postanıza gelen bağlantıyla adresinizi doğrulayın. Deneme planıyla hemen ders başlatabilirsiniz.' },
];

/** Hareketli örnek: öğretmenin çizgisi, aynı anda öğrencinin ekranında da çizilir. */
function Worksheet({ delay = false }: { delay?: boolean }) {
  return (
    <svg viewBox="0 0 360 236" className={`lp-sheet ${delay ? 'is-student' : ''}`} aria-hidden="true">
      <rect width="360" height="236" fill="#fff" />
      <rect width="360" height="30" fill="#f3f4fb" />
      <text x="18" y="20" className="lp-sheet-title">
        Çalışma Kâğıdı · Sayfa 1
      </text>
      <path className="lp-hl lp-draw" d="M112 54 L218 54" pathLength={1} />
      <text x="18" y="58" className="lp-sheet-q">
        1. 2x + 3 = 11 ise x kaçtır?
      </text>
      <text x="34" y="88" className="lp-sheet-a">
        A) 2
      </text>
      <text x="104" y="88" className="lp-sheet-a">
        B) 3
      </text>
      <text x="174" y="88" className="lp-sheet-a">
        C) 4
      </text>
      <text x="244" y="88" className="lp-sheet-a">
        D) 7
      </text>
      <path className="lp-ink lp-draw lp-d2" d="M201 76 C190 69 169 71 167 83 C165 95 188 98 201 91 C210 86 208 76 197 73" pathLength={1} />
      <path className="lp-ink lp-draw lp-d3" d="M176 102 C170 122 150 136 118 148 M130 136 L118 148 L134 151" pathLength={1} />
      <text x="34" y="180" className="lp-hand lp-fade lp-d4">
        2x = 8 ⇒ x = 4 ✓
      </text>
      <path className="lp-ink-2 lp-draw lp-d4" d="M34 191 L196 191" pathLength={1} />
    </svg>
  );
}

function LiveDemo() {
  return (
    <div className="lp-demo" role="img" aria-label="Öğretmenin kalemle yazdığı çözüm aynı anda öğrencinin ekranında görünüyor">
      <figure className="lp-device lp-teacher">
        <figcaption>
          <span className="lp-live" /> Öğretmen ekranı
        </figcaption>
        <Worksheet />
      </figure>
      <figure className="lp-device lp-student">
        <figcaption>Öğrenci ekranı</figcaption>
        <Worksheet delay />
      </figure>
    </div>
  );
}

/** Ürünün kendi formül motorunun çıktısı: derste de birebir böyle çizilir. */
function FigureSvg({ figure, label }: { figure: Figure; label: string }) {
  return (
    <svg className="lp-figure" style={{ width: figure.w + 4 }} viewBox={`-2 -2 ${figure.w + 4} ${figure.h + 4}`} role="img" aria-label={label}>
      {figure.lines.map(([x1, y1, x2, y2, w], i) => (
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} strokeWidth={w} />
      ))}
      {figure.texts.map((t, i) => (
        <text key={i} x={t.x} y={t.y} fontSize={t.s}>
          {t.t}
        </text>
      ))}
    </svg>
  );
}

const SHOWN_POLYGONS: PolygonKind[] = ['triangle', 'trapezoid', 'pentagon', 'hexagon'];

function MathShowcase() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true;
    const done = () => alive && setReady(true);
    if (document.fonts) document.fonts.load(`20px "${ANNOTATION_FONT}"`).then(done, done);
    else done();
    return () => {
      alive = false;
    };
  }, []);
  const figures = useMemo(() => {
    if (!ready) return null;
    return {
      integral: layoutFormula('integral', { upper: '1', lower: '0', body: 'x² dx' }, 22, 1.6, measureText),
      sum: layoutFormula('sum', { upper: 'n', lower: 'k = 1', body: 'k²' }, 22, 1.6, measureText),
      fraction: layoutFormula('fraction', { top: 'x + 1', bottom: 'x − 1' }, 22, 1.6, measureText),
      root: layoutFormula('root', { index: '', body: 'x² + 1' }, 22, 1.6, measureText),
      line: layoutNumberLine({ min: -5, max: 5, step: 1, length: 460 }, 16, 1.6, measureText),
      plane: layoutCoordinatePlane({ xMin: -4, xMax: 4, yMin: -3, yMax: 3, cell: 30 }, 16, 1.6, measureText),
    };
  }, [ready]);

  return (
    <div className="lp-math">
      <div className="lp-math-card lp-span-2">
        <h3>Hazır formüller</h3>
        <div className="lp-formulas">
          {figures && (
            <>
              <FigureSvg figure={figures.integral} label="Sıfırdan bire x kare dx integrali" />
              <FigureSvg figure={figures.sum} label="k eşittir birden n’e k kare toplamı" />
              <FigureSvg figure={figures.fraction} label="x artı bir bölü x eksi bir" />
              <FigureSvg figure={figures.root} label="x kare artı birin karekökü" />
            </>
          )}
        </div>
      </div>
      <div className="lp-math-card lp-plane">
        <h3>Koordinat düzlemi</h3>
        {figures && <FigureSvg figure={figures.plane} label="Kareli koordinat düzlemi" />}
      </div>
      <div className="lp-math-card lp-span-2">
        <h3>Sayı doğrusu</h3>
        {figures && <FigureSvg figure={figures.line} label="Eksi beşten beşe sayı doğrusu" />}
      </div>
      <div className="lp-math-card">
        <h3>Geometrik şekiller</h3>
        <div className="lp-polygons">
          {SHOWN_POLYGONS.map((kind) => (
            <svg key={kind} viewBox="-2 -2 44 44" role="img" aria-label={POLYGONS[kind].label}>
              <polygon points={POLYGONS[kind].vertices.map(([u, v]) => `${u * 40},${v * 40}`).join(' ')} />
            </svg>
          ))}
        </div>
      </div>
      <div className="lp-math-card lp-span-2 lp-symbols" aria-label="Hazır matematik işaretleri">
        {['∑', '∫', '∂', '√', '∞', 'π', 'θ', '≤', '≥', '≠', '≈', '±', '∠', '⊥', '∥', '∈', '∪', '∩', 'ℝ', '⇒'].map((s) => (
          <span key={s}>{s}</span>
        ))}
        <small>ve daha fazlası</small>
      </div>
    </div>
  );
}

function JoinForm() {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const id = extractLessonId(code);
    if (id) location.href = `/ders/${id}`;
    else setError('Ders kodunu ya da bağlantısını kontrol edin.');
  };
  return (
    <form className="lp-join-form" onSubmit={submit}>
      <label htmlFor="join-code">Ders kodu veya bağlantı</label>
      <div className="lp-join-row">
        <input id="join-code" value={code} placeholder="ör. ax4cb5qh" autoComplete="off" onChange={(e) => setCode(e.target.value)} />
        <button type="submit" className="btn btn-primary btn-lg" disabled={!code.trim()}>
          Katıl <LuArrowRight />
        </button>
      </div>
      {error && <p className="form-error">{error}</p>}
    </form>
  );
}

export function Landing() {
  return (
    <div className="lp">
      <header className="lp-header">
        <a className="brand lp-brand" href="/">
          <NovaMark size={32} />
          <span className="brand-name">Nova Class</span>
        </a>
        <nav className="lp-nav" aria-label="Sayfa bölümleri">
          <a href="#neden">Neden</a>
          <a href="#nasil">Nasıl çalışır</a>
          <a href="#ozellikler">Özellikler</a>
          <a href="#sss">Sorular</a>
        </nav>
        <div className="lp-auth">
          <a className="btn lp-signin" href={LOGIN_PATH}>
            Giriş yap
          </a>
          <a className="btn lp-login" href={SIGNUP_PATH}>
            Hesap aç
          </a>
        </div>
      </header>

      <section className="lp-hero">
        <div className="lp-hero-copy">
          <p className="lp-eyebrow">PDF üzerinde canlı ders</p>
          <h1>Kalemle anlatın. Öğrencileriniz aynı anda görsün.</h1>
          <p className="lp-lead">
            Nova Class, ders PDF’nizi canlı bir tahtaya çevirir. Yazdığınız her çizgi, kalemi kaldırmadan öğrencilerin ekranına ulaşır. Video akışı yok, bulanık ekran yok.
          </p>
          <div className="lp-cta">
            <a className="btn btn-lg lp-primary" href={SIGNUP_PATH}>
              Öğretmen hesabı aç <LuArrowRight />
            </a>
            <a className="btn btn-lg lp-ghost" href="#derse-katil">
              Derse katıl
            </a>
          </div>
        </div>
        <LiveDemo />
      </section>

      <main>
        <section className="lp-section" id="neden">
          <p className="lp-kicker">Neye çözüm?</p>
          <h2>Ekran paylaşımı, kalemle anlatılan bir ders için tasarlanmadı</h2>
          <div className="lp-pains">
            {PAINS.map((p) => (
              <article key={p.problem} className="lp-pain">
                <p className="lp-problem">{p.problem}</p>
                <p className="lp-answer">{p.answer}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="lp-section lp-tint" id="nasil">
          <div className="lp-inner">
            <p className="lp-kicker">Nasıl çalışır?</p>
            <h2>Üç adımda derse başlayın</h2>
            <ol className="lp-steps">
              {STEPS.map((s, i) => (
                <li key={s.title}>
                  <span className="lp-step-no">{i + 1}</span>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="lp-section" id="ozellikler">
          <p className="lp-kicker">Özellikler</p>
          <h2>Bir derste ihtiyacınız olan araçlar</h2>
          <div className="lp-features">
            {FEATURES.map((f) => (
              <article key={f.title}>
                <f.icon />
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="lp-section lp-tint">
          <div className="lp-inner">
            <p className="lp-kicker">Matematik için</p>
            <h2>Formülü elle çizmekle vakit kaybetmeyin</h2>
            <p className="lp-sub">Aşağıdakiler ekran görüntüsü değil; Nova Class’ın derste çizdiği şekillerin kendisi. Hepsi öğrencilere canlı gider ve PDF’ye işlenir.</p>
            <MathShowcase />
          </div>
        </section>

        <section className="lp-section lp-split" id="derse-katil">
          <div>
            <p className="lp-kicker">Öğrenci misiniz?</p>
            <h2>Hesap açmadan derse katılın</h2>
            <p className="lp-sub">Öğretmeninizin paylaştığı bağlantıyı açın ya da ders kodunu buraya yazın.</p>
          </div>
          <JoinForm />
        </section>

        <section className="lp-section lp-tint" id="sss">
          <div className="lp-inner lp-narrow">
            <p className="lp-kicker">Sık sorulanlar</p>
            <h2>Merak edilenler</h2>
            {FAQ.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="lp-final">
          <h2>Bir sonraki dersinizi Nova Class’ta anlatın</h2>
          <div className="lp-cta">
            <a className="btn btn-lg lp-primary" href={SIGNUP_PATH}>
              Öğretmen hesabı aç <LuArrowRight />
            </a>
            <a className="btn btn-lg lp-ghost" href={LOGIN_PATH}>
              Giriş yap
            </a>
          </div>
        </section>
      </main>

      <footer className="lp-footer">
        <span className="brand">
          <NovaMark size={24} />
          <span className="brand-name">Nova Class</span>
        </span>
        <nav className="lp-legal" aria-label="Hukuki metinler">
          <a href="/gizlilik">Gizlilik</a>
          <a href="/kvkk">KVKK</a>
          <a href="/kullanim-kosullari">Kullanım Koşulları</a>
        </nav>
        <span className="muted small">
          Bir <a href={SUPERNOVA_URL}>Supernova Academy</a> ürünü
        </span>
      </footer>
    </div>
  );
}
